import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Button, Alert, IconButton, Menu, MenuItem } from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import { login, logout, clearError } from '../../store/slices/authSlice';
import { AppDispatch, RootState } from '../../store';
import { LoginCredentials } from '../../types';
import { checkAuth } from '../../api/auth';
import { AxiosError } from 'axios';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const error = useSelector((state: RootState) => state.auth.error);

  useEffect(() => {
    const isE2ETest = (window as any).__E2E_TEST__;
    if (process.env.NODE_ENV === 'test' || isE2ETest) {
      return;
    }

    const checkAuthentication = async () => {
      if (isLoggingOut) {
        return;
      }

      try {
        await checkAuth();
        if (isAuthenticated && user) {
          navigate('/dashboard');
        }
      } catch (err) {
        // 認証チェックでのエラーは無視（ログインページなので正常）
      }
    };

    checkAuthentication();
    // コンポーネントアンマウント時のみクリア
    return () => {
      dispatch(clearError());
    };
  }, [navigate, dispatch, isAuthenticated, user, isLoggingOut]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());

    try {
      const credentials: LoginCredentials = { email, password };
      console.log('🚀 Redux login dispatch開始');
      const result = await dispatch(login(credentials)).unwrap();
      console.log('✅ Redux login成功, result:', result);
      console.log('🔄 ダッシュボードへリダイレクト');
      navigate('/dashboard');
    } catch (err) {
      // エラーはReduxのstate.auth.errorで管理される
      console.log('❌ Redux login失敗:', err);
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await dispatch(logout()).unwrap();
      handleClose();
      navigate('/login', { replace: true });
    } catch (error) {
      // ログアウトに失敗した場合の処理
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }} data-testid="login-form">
      <Box sx={{ mb: 2 }}>
        {error && (
          <Alert
            severity="error"
            data-testid="error-message"
            sx={{
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '48px',
              position: 'relative',
              zIndex: 1,
              visibility: 'visible',
              display: 'flex',
              opacity: 1,
            }}
          >
            {error}
          </Alert>
        )}
      </Box>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="メールアドレス"
        name="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="email-input"
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="パスワード"
        type="password"
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="password-input"
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        data-testid="login-button"
      >
        ログイン
      </Button>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        data-testid="user-menu"
      >
        <AccountCircle />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleLogout} data-testid="logout-button">
          ログアウト
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LoginForm;
